import Switch, { SwitchProps } from '@mui/material/Switch';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import { FieldValues, useController } from 'react-hook-form';
import { SxProps } from '@mui/material/styles';

import { TControllerProps } from 'common/types';
import { asTypedControl } from 'common/utils/form';
import { withSxProp } from 'common/utils/props';

const formControlLabelStyle: SxProps = {
    '.MuiTypography-root': {
        fontWeight: 700,
        fontSize: '1.6rem',
    },
};

const switchStyle: SxProps = {
    '.MuiSwitch-switchBase ': {
        '+ .MuiSwitch-track': {
            backgroundColor: 'primary.light',
        },

        '&.Mui-checked': {
            color: 'primary.main',
        },
    },
};

interface IProps extends SwitchProps {
    helperText?: string;
    label?: string;
    labelSx?: SxProps;
    formControlProps?: React.ComponentProps<typeof FormControl>;
    name: string;
}

export const SwitchField = <TFieldValues extends FieldValues>({
    control: formControl,
    disabled,
    helperText,
    label,
    labelSx,
    formControlProps,
    name,
    sx,
    ...other
}: IProps & TControllerProps<TFieldValues>) => {
    const control = asTypedControl<boolean>(name, formControl);

    const {
        field,
        fieldState: { error },
    } = useController({
        name,
        control,
    });

    const { value } = field;

    return (
        <FormControl
            fullWidth
            margin="dense"
            variant="standard"
            {...formControlProps}
        >
            <FormControlLabel
                control={
                    <Switch
                        {...field}
                        checked={value || false}
                        disabled={disabled}
                        sx={[switchStyle, ...withSxProp(sx)]}
                        tabIndex={-1}
                        type="checkbox"
                        {...other}
                    />
                }
                label={label}
                sx={[formControlLabelStyle, ...withSxProp(labelSx)]}
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key !== ' ' || disabled) return;

                    const { onChange } = field;

                    onChange(!value);
                }}
            />

            {!!helperText && (
                <FormHelperText sx={{ ml: 6 }}>{helperText}</FormHelperText>
            )}

            {!!error?.message && (
                <FormHelperText error>{error.message}</FormHelperText>
            )}
        </FormControl>
    );
};
